import { formatEur, type MemberDto } from "@gladis/shared";
import { firstNameOf, StatCard, StatRow, type StatCardRow, type StatTrend } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { IncomePageDto } from "../dto.js";

/**
 * The three figures above the chart.
 *
 * ==========================================================================================
 * THE THIRD CARD IS THE ONE THE MODULE EXISTS FOR
 * ==========================================================================================
 *
 * "Received this year" against the SAME POINT last year is what answers "is it going up", and it is
 * the reason a source keeps a dated amount history at all.
 *
 * The comparison is to the same point, never to the whole of last year — the server computes both
 * sides over the same number of elapsed months. Against a full previous year the same data reports a
 * catastrophic fall every January and stays wrong until roughly December.
 *
 * IT IS ABSENT, NOT ZERO, when there is nothing to compare against. A household whose earliest income
 * is in the current year has no previous year; a comparison against zero would report infinite
 * growth, and "€0,00 last year" would assert something the app does not know. Savings' change-this-
 * month card is absent for a household created this month, for the same reason.
 *
 * A CHANGE OF EXACTLY ZERO IS SHOWN. A flat year is a finding, and it is a different answer from
 * "there is nothing to compare".
 */

function trendOf(change: number): StatTrend {
  if (change > 0) return "up";
  if (change < 0) return "down";
  return "level";
}

export interface IncomeStatsProps {
  page: IncomePageDto;
  /** The household's members, for their faces (020). */
  members?: readonly MemberDto[];
}

export function IncomeStats({ page, members = [] }: IncomeStatsProps): ReactNode {
  const t = useT();
  /**
   * One row per member with income of their own, plus one for joint.
   *
   * JOINT INCOME IS NOT DIVIDED between members — dividing it would assert a contribution ratio
   * nothing in the app records. It sits beside them as its own row, which is savings' FR-S6a applied
   * to money coming in, and the rows sum to the headline exactly.
   */
  const earnedBy: StatCardRow[] = page.earnedBy.map((row) => ({
    key: row.memberId ?? "joint",
    /*
      `displayName` IS NULL FOR A ROW THAT IS NOT ABOUT A NAMED PERSON — feature 021.

      It used to be the literal word "Joint", composed in `earned-by.ts`, and this line carried a
      comment saying that word "has no surname to drop either way". That was true and it was the
      wrong place for the word to exist: a domain service cannot reach a catalogue, so half this
      household read it in a language they do not use.

      Two rows arrive nameless and `memberId` tells them apart: null is the household's own income,
      and a member id with no name is an owner who left without a snapshot.
    */
    label:
      row.displayName === null
        ? t(row.memberId === null ? "person.joint" : "person.formerMember")
        : firstNameOf(row.displayName),
    value: formatEur(row.monthlyInEuros),
    /*
      Joint wears everybody; a named member wears their own face; a member the household can no
      longer identify wears NOTHING, which is 020's FR-024 — initials for a stand-in render cleanly,
      read as data and fail no test, so "AM" beside "A former member" is the mistake to avoid.
    */
    ...(row.memberId === null
      ? {
          avatarGroup: members.map((m) => ({ name: m.displayName, avatarUrl: m.avatarUrl })),
        }
      : row.displayName === null
        ? {}
        : {
            // The FULL name for the circle, so it still says "AL" beside the word "Ada". Without
            // this the initials fall back to the label, which is now a first name, and every avatar
            // in the card loses the letter that tells two members apart.
            avatarName: row.displayName,
            avatarUrl: members.find((m) => m.userId === row.memberId)?.avatarUrl ?? null,
          }),
  }));

  const change = page.thisYear.changeInEuros;
  const comparable = change !== null && page.thisYear.samePointLastYearInEuros !== null;

  return (
    <StatRow>
      <StatCard
        label={t("inc.stats.monthly")}
        value={formatEur(page.monthlyIncomeInEuros)}
        hint={t("inc.stats.monthlyHint")}
        testId="income-monthly"
        valueTestId="income-monthly-value"
      />

      <StatCard
        label={t("inc.stats.howEarned")}
        rows={earnedBy}
        hint={earnedBy.length === 0 ? t("inc.stats.noneYet") : undefined}
        testId="income-earned-by"
      />

      <StatCard
        label={t("inc.stats.receivedThisYear")}
        value={formatEur(page.thisYear.receivedInEuros)}
        {...(comparable ? { trend: trendOf(change) } : {})}
        hint={
          comparable ? (
            <>
              {/*
               * The delta carries a GLYPH AND A SIGN rather than a colour — 009's rule, and it holds
               * for the same reason: there is no status palette to draw a red/green pair from, and
               * inventing one in the corner of a card is a design-system decision made in the wrong
               * place. Whether income rising is good news is also not this component's business.
               */}
              {change >= 0 ? "+" : "−"}
              {formatEur(Math.abs(change))} against{" "}
              {formatEur(page.thisYear.samePointLastYearInEuros ?? 0)} by this point last year
            </>
          ) : (
            t("inc.stats.noEarlierYear")
          )
        }
        testId="income-this-year"
        valueTestId="income-this-year-value"
      />
    </StatRow>
  );
}
